import React, { useCallback, useRef, useState } from 'react';
import { ATMButton, ATMModal } from 'shared-it-appmod-ui';
import {
  ToastError,
  ToastSuccess,
} from 'src/components/atoms/toaster/toaster.component';
import Lang from 'src/libraries/language';
import { useAdminContext } from 'src/contexts/admin.context';
import { useEmployeeContext } from 'src/contexts/employee.context';
import { IAdminForm } from 'src/models/admin.model';
import { IEmployee } from 'src/models/employee.model';
import { getAdminStatus } from 'src/selectors/admin.selector';
import { actionTypes } from 'src/ducks/admin.duck';
import RoleMaintenanceForm from '../role-maintenance-form/role-maintenance-form.component';
import style from '../role-maintenance-list/role-maintenance-list.module.scss';

const RoleMaintenanceAdd: React.FC = () => {
  const { state, actions } = useAdminContext();
  const { state: employeeState } = useEmployeeContext();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [employee, setEmployee] = useState<IEmployee>();
  const [isDirty, setIsDirty] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const defaultValues: Partial<IAdminForm> = {
    empNo: '',
    roleId: undefined,
  };

  const addStatus = getAdminStatus(state, actionTypes.ADMIN_DATA_CREATE);

  const handleSave = useCallback(() => {
    if (formRef && formRef.current) {
      formRef.current.handleSubmit();
    }
  }, [formRef]);

  const handleSubmit = useCallback(
    async (data) => {
      const formData: IAdminForm = {
        empNo: data.empNo,
        roleId: data.roleId,
      };
      const response = await actions.createPOST(formData);
      if (!response.error) {
        ToastSuccess(
          Lang.formatString(
            Lang.MSG_ADMIN_SUCCESS_CREATE,
            `${employee?.last_name}, ${employee?.first_name}`
          )
        );
      } else {
        ToastError(response.error.message);
      }
      setIsAddOpen(false);
      setEmployee(undefined);
      actions.listGET();
    },
    [actions, employee, setIsAddOpen, setEmployee]
  );

  const handleSearch = useCallback(
    (id) => {
      const user = employeeState.list.find((value) => value.emp_no === id);

      setEmployee(user);
    },
    [employeeState, setEmployee]
  );

  return (
    <ATMModal
      open={isAddOpen}
      size="tiny"
      trigger={
        <ATMButton
          basic
          color="blue"
          content={Lang.LBL_NEW}
          icon="plus"
          floated="right"
          onClick={() => setIsAddOpen(true)}
        />
      }
      header={Lang.LBL_ADMIN_ADD}
      content={
        <div className={style.content}>
          <RoleMaintenanceForm
            defaultValues={defaultValues}
            formRef={formRef}
            employee={employee}
            handleSubmit={handleSubmit}
            handleSearch={handleSearch}
            handleEnable={(value) => setIsDirty(value)}
          />
        </div>
      }
      actions={
        <div className={style.actionBtns}>
          <ATMButton
            basic
            color="blue"
            content={Lang.LBL_CANCEL}
            onClick={() => {
              setIsAddOpen(false);
              setEmployee(undefined);
            }}
          />
          <ATMButton
            primary
            content={Lang.LBL_SAVE}
            disabled={!isDirty || addStatus.fetching}
            onClick={handleSave}
            loading={addStatus.fetching}
          />
        </div>
      }
    />
  );
};

export default RoleMaintenanceAdd;
